import { memo } from 'react';
import { JobRowActions } from '@/components/job-feed/JobRowActions';
import type { JobRowProps } from '@/components/job-feed/jobFeed.types';
import { ScoreWithTooltip } from '@/components/ui/ScoreWithTooltip';
import { StatusGroup } from '@/components/ui/StatusGroup';
import type { StatusPillStatus } from '@/components/ui/StatusPill';
import { cx } from '@/cva.config';
import { breakdownFromJob } from '@/lib/jobAdapter';
import { formatSalary } from '@/lib/score';
import {
  useHasSelection,
  useIsJobSelected,
  useSelectJob,
} from '@/providers/dashboard/JobBrowserContext';

export const JobRowCompact = memo(({ job }: JobRowProps) => {
  const selected = useIsJobSelected(job.id);
  const hasSelection = useHasSelection();
  const select = useSelectJob();
  const hideActions = hasSelection;
  const tooltipsDisabled = hasSelection;

  const salary = formatSalary(job.salaryMin, job.salaryMax);
  const breakdown = breakdownFromJob(job);

  return (
    // biome-ignore lint/a11y/useSemanticElements: the row contains its own action buttons, so it cannot be a native <button> (nested buttons are invalid HTML); role="button" + keyboard handling is the correct pattern here
    <div
      className={cx(
        'group grid cursor-pointer grid-cols-[96px_2.4fr_1fr_1fr_110px_230px_70px] items-center gap-3 border-b border-border/60 px-4 py-1.5 transition-colors hover:bg-border/20',
        selected && 'bg-accent-soft/50 hover:bg-accent-soft/60',
        hideActions && 'grid-cols-[96px_2.4fr_1fr_1fr_110px_230px]',
      )}
      data-job-id={job.id}
      onClick={() => select(job.id)}
      onKeyDown={(event) => {
        if (event.target !== event.currentTarget) return;
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          select(job.id);
        }
      }}
      role="button"
      tabIndex={0}
    >
      <ScoreWithTooltip
        breakdown={breakdown}
        disabled={tooltipsDisabled}
        placement="right"
        score={job.matchScore}
      />
      <div className="flex min-w-0 items-center gap-2">
        <span className="truncate text-sm lg:text-base font-medium">
          {job.title}
        </span>
        <span className="truncate text-xs lg:text-sm text-foreground-muted">
          {job.company}
        </span>
      </div>
      <span className="truncate text-xs lg:text-sm text-foreground-muted">
        {job.location ?? '—'}
      </span>
      <span
        className={cx(
          'truncate font-mono text-xs lg:text-sm text-subtle tabular-nums',
          salary && 'text-score-high',
        )}
      >
        {salary ?? '—'}
      </span>
      <span className="truncate font-mono text-2xs lg:text-xs tracking-wide text-subtle uppercase">
        {job.source}
      </span>
      <span className="flex justify-start">
        <StatusGroup
          appStatus={
            job.appStatus ? (job.appStatus as StatusPillStatus) : undefined
          }
          jobStatus={job.status as StatusPillStatus}
        />
      </span>
      {!hideActions && <JobRowActions job={job} variant="compact" />}
    </div>
  );
});

JobRowCompact.displayName = 'JobRowCompact';
